/* eslint-disable no-useless-escape */
import { PureComponent, cloneElement, isValidElement } from 'react';
import classNames from 'classnames';
import { maskValue, unmaskValue, coerceToString, getOnlyChild } from './maskHelpers';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// TODO: Remove `maskValue` and `unmaskValue` exports with next major release (v3.x.x)
export { maskValue, unmaskValue };

/**
 * Note: Chrome appends a /v modifier to regular expressions, which enables all unicode character set features. As a result,
 * - and { } characters must be escaped.
 */
const maskPattern = {
  phone: '[0-9\\-]*',
  ssn: '[0-9\\-*]*',
  zip: '[0-9\\-]*',
  currency: '[0-9.,\\-]*'
};
const maskOverlayContent = {
  currency: '$'
};
export class Mask extends PureComponent {
  constructor(props) {
    super(props);
    const field = getOnlyChild(this.props.children);
    const initialValue = coerceToString(field.props.value ?? field.props.defaultValue);
    this.state = {
      value: maskValue(initialValue, props.mask)
    };
  }
  componentDidUpdate(prevProps) {
    if (this.debouncedOnBlurEvent) {
      getOnlyChild(this.props.children).props.onBlur?.(this.debouncedOnBlurEvent);
      this.debouncedOnBlurEvent = null;
    }
    const field = getOnlyChild(this.props.children);
    const fieldProps = field.props;
    const prevField = getOnlyChild(prevProps.children);
    const prevFieldValue = /*#__PURE__*/isValidElement(prevField) ? coerceToString(prevField.props.value) : '';
    const fieldValue = coerceToString(fieldProps.value);
    const isControlled = fieldProps.value !== undefined;
    if (isControlled && prevFieldValue !== fieldValue) {
      const {
        mask
      } = this.props;
      // For controlled components, the value prop should ideally be changed by
      // the controlling component once we've called onChange with our updates.
      // If the change was triggered this way through user input, then the prop
      // given should match our internal state when unmasked. If what we're
      // given and what we have locally don't match, that means the controlling
      // component has made its own unrelated change, so we should update our
      // state and mask this new value.

      if (unmaskValue(fieldValue, mask) !== unmaskValue(this.state.value, mask)) {
        const value = maskValue(fieldValue, mask);
        this.setState({
          value
        });
      }
    }
  }
  /**
   * To avoid a jarring experience for screen readers, we only
   * add/remove characters after the field has been blurred,
   * rather than when the user is typing in the field
   * @param {Object} evt
   * @param {React.Element} field - Child TextField
   */
  handleBlur(evt, field) {
    const value = maskValue(evt.target.value, this.props.mask);

    // We only debounce the onBlur when we know for sure that
    // this component will re-render (AKA when the value changes)
    // and when an onBlur callback is present
    const debounce = value !== this.state.value && typeof field.props.onBlur === 'function';
    if (debounce) {
      // We need to retain a reference to the event after the callback
      // has been called. We pass this onto the consuming app's onBlur
      // only after the value has been manipulated – this way, the
      // value returned by event.target.value is the value after masking
      evt.persist();
      this.debouncedOnBlurEvent = evt;
    }
    this.setState({
      value
    });
    if (!debounce && typeof field.props.onBlur === 'function') {
      // If we didn't debounce the onBlur event, then we need to
      // call the onBlur callback from here
      field.props.onBlur(evt);
    }
  }

  /**
   * @param {Object} evt
   * @param {React.Element} field - Child TextField
   */
  handleChange(evt, field) {
    this.setState({
      value: evt.target.value
    });
    if (typeof field.props.onChange === 'function') {
      field.props.onChange(evt);
    }
  }
  render() {
    const {
      mask
    } = this.props;
    const field = getOnlyChild(this.props.children);
    const modifiedTextField = /*#__PURE__*/cloneElement(field, {
      defaultValue: undefined,
      fieldClassName: classNames(field.props.fieldClassName, `ds-c-field--${mask}`),
      onBlur: evt => this.handleBlur(evt, field),
      onChange: evt => this.handleChange(evt, field),
      value: this.state.value,
      type: 'text',
      inputMode: 'numeric',
      pattern: maskPattern[this.props.mask]
    });

    // UI overlayed on top of a field to support certain masks
    const maskOverlay = maskOverlayContent[mask] ? /*#__PURE__*/_jsx("div", {
      className: `ds-c-field__before ds-c-field__before--${mask}`,
      children: maskOverlayContent[mask]
    }) : null;
    return /*#__PURE__*/_jsxs("div", {
      className: `ds-c-field-mask ds-c-field-mask--${mask}`,
      children: [maskOverlay, modifiedTextField]
    });
  }
}
export default Mask;